import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";
import { api, C, errorMessage } from "../lib/api";

const ok = (p) => p.length >= 6 && /\d/.test(p) && /[a-zA-Z]/.test(p);

// /reset?token=… — the page the emailed link opens: choose a new password, confirm it, done.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const match = p1.length > 0 && p1 === p2;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(C.ApiRoutes.AUTH_RESET_PASSWORD, { token, password: p1 });
      setDone(true);
    } catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 noise" data-testid="reset-page">
      <div className="glass rounded-3xl p-8 w-full max-w-md flex flex-col gap-5 rise">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] gold-text">Players Room</div>
          <h1 className="text-2xl font-display mt-1 flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" /> New password</h1>
        </div>

        {!token && <div data-testid="reset-no-token" className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-3">This page needs the link from your email. Open the link again, or request a new one from the sign-in page.</div>}

        {token && !done && (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-xs text-slate-400">Create new password
              <input data-testid="reset-password" type="password" required autoFocus value={p1} onChange={(e) => setP1(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Verify password
              <input data-testid="reset-password-2" type="password" required value={p2} onChange={(e) => setP2(e.target.value)}
                className={`bg-black/40 border rounded-lg px-3 py-3 text-base text-slate-100 outline-none ${p2 && !match ? "border-red-500/60" : "border-white/10 focus:border-amber-400"}`} />
            </label>
            <div className="text-[11px] flex flex-col gap-1">
              <span data-testid="reset-rule" className={ok(p1) ? "text-emerald-300" : "text-slate-400"}>{ok(p1) ? "✓" : "·"} At least 6 characters, with one number and one letter</span>
              <span data-testid="reset-match" className={p2 ? (match ? "text-emerald-300" : "text-red-300") : "text-slate-400"}>{p2 ? (match ? "✓ Passwords match" : "× Passwords do not match") : "· Both passwords must match"}</span>
            </div>
            {err && <div data-testid="reset-error" className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="reset-submit" disabled={busy || !ok(p1) || !match} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift disabled:opacity-50">{busy ? "…" : "SAVE NEW PASSWORD"}</button>
          </form>
        )}

        {done && !dismissed && (
          <div className="flex flex-col gap-4" data-testid="reset-done">
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-4 text-sm text-emerald-100 flex gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-300" />
              <div>Your password has been changed. Your old password no longer works, and any other devices you were signed in on have been signed out.<br /><br />Return to the sign-in page and sign in with your email and your new password.</div>
            </div>
            <div className="flex gap-2">
              <Link to="/" data-testid="reset-to-signin" className="flex-1 text-center rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift">GO TO SIGN IN</Link>
              <button data-testid="reset-dismiss" onClick={() => setDismissed(true)} className="rounded-full px-5 py-3 font-display text-sm bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10">DISMISS</button>
            </div>
          </div>
        )}

        {done && dismissed && <div data-testid="reset-dismissed" className="text-sm text-slate-400">You can close this tab. Sign in again at <Link to="/" className="text-amber-300 underline underline-offset-4">playersroom.au</Link>.</div>}

        <div className="text-[11px] text-slate-500 flex items-center gap-1.5 border-t border-white/10 pt-3"><ShieldCheck className="w-3.5 h-3.5" /> Links work once and expire an hour after they are sent.</div>
      </div>
    </div>
  );
}
